import { useState, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box, Container, Grid, Paper, Typography, Button, Chip,
  Avatar, IconButton, Tabs, Tab, Card, CardContent,
  CardMedia, CardActions, Skeleton, Alert, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, useMediaQuery, useTheme, Divider,
  Tooltip, CircularProgress, Badge, AppBar, Toolbar
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Schedule as ScheduleIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon,
  Language as WebsiteIcon,
  ArrowBack as BackIcon,
  Refresh as RefreshIcon,
  Image as ImageIcon,
  QuestionAnswer as FaqIcon,
  RateReview as ReviewIcon,
  Person as BioIcon,
  Campaign as PostIcon,
  MoreVert as MoreIcon,
  LinkOff as NoAccountIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Restore as RestoreIcon
} from '@mui/icons-material';
import {
  useGetDraftsQuery,
  useApproveDraftMutation,
  useRejectDraftMutation,
  useEditDraftMutation,
} from '../../redux/thunks/drafts/getdrafts';
import { useGetSocialAccountsQuery } from '../../redux/thunks/ai/getsocialaccounts';
import { useGetBusinessOwnerViewQuery } from '../../redux/thunks/sp/getbusinessownerview';
import { useGetBusinessByIdQuery } from '../../redux/thunks/unauth/getbusinessbyid';
import usePollingInterval from '../../hooks/usePollingInterval';
// ANNOUNCEMENT: "From your update" on drafts made from an owner announcement
import { AnnouncementItems } from '../Announcements/announcementFormat';


// ===== Constants =====

const MAX_CONTENT_LENGTH = 20000;   // mirrors editdraft.js

const PLATFORM_CONFIG = {
  facebook:  { icon: <FacebookIcon />,  color: '#1877F2', label: 'Facebook' },
  instagram: { icon: <InstagramIcon />, color: '#E4405F', label: 'Instagram' },
  linkedin:  { icon: <LinkedInIcon />,  color: '#0A66C2', label: 'LinkedIn' },
  twitter:   { icon: <TwitterIcon />,   color: '#1DA1F2', label: 'Twitter' },
  website:   { icon: <WebsiteIcon />,   color: '#4CAF50', label: 'Website' },
};

const DRAFT_TYPE_CONFIG = {
  social_post:     { icon: <PostIcon />,   label: 'Social Post',      color: 'primary' },
  faq:             { icon: <FaqIcon />,    label: 'FAQ',               color: 'info' },
  review_response: { icon: <ReviewIcon />, label: 'Review Response',   color: 'warning' },
  bio:             { icon: <BioIcon />,    label: 'Business Bio',      color: 'success' },
};

// STATUS: approved_pending_connection and publish_failed were missing, so
// those drafts (6 and 4 in prod, Oct 2026) were invisible to their owners.
const STATUS_TABS = [
  { value: 'pending_approval',            label: 'Pending',            color: 'warning' },
  { value: 'scheduled',                   label: 'Scheduled',          color: 'info' },
  { value: 'approved_pending_connection', label: 'Waiting to connect', color: 'warning' },
  { value: 'published',                   label: 'Published',          color: 'success' },
  { value: 'publish_failed',              label: 'Failed to publish',  color: 'error' },
  { value: 'rejected',                    label: 'Rejected',           color: 'error' },
  // ANNOUNCEMENT: drafts whose moment passed before review, or before publishing.
  { value: 'expired',                     label: 'Expired',            color: 'default' },
];

// ===== Sub-components =====

const PlatformChip = ({ platform, size = 'small' }) => {
  const config = PLATFORM_CONFIG[platform] || PLATFORM_CONFIG.website;
  return (
    <Chip
      icon={config.icon}
      label={config.label}
      size={size}
      sx={{
        bgcolor: `${config.color}15`,
        color: config.color,
        border: `1px solid ${config.color}40`,
        '& .MuiChip-icon': { color: config.color }
      }}
    />
  );
};

const DraftTypeBadge = ({ draftType }) => {
  const config = DRAFT_TYPE_CONFIG[draftType] || DRAFT_TYPE_CONFIG.social_post;
  return (
    <Chip
      icon={config.icon}
      label={config.label}
      size="small"
      color={config.color}
      variant="outlined"
    />
  );
};

const NoAccountWarning = ({ platform }) => (
  <Tooltip title={`No default ${platform} account connected. Connect one in settings to publish.`}>
    <Chip
      icon={<NoAccountIcon />}
      label="No account connected"
      size="small"
      color="warning"
      variant="outlined"
    />
  </Tooltip>
);

const DraftCardSkeleton = () => (
  <Card variant="outlined" sx={{ mb: 2 }}>
    <CardContent>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Skeleton variant="rounded" width={80} height={24} />
        <Skeleton variant="rounded" width={100} height={24} />
      </Box>
      <Skeleton variant="text" width="100%" height={20} />
      <Skeleton variant="text" width="90%" height={20} />
      <Skeleton variant="text" width="60%" height={20} />
    </CardContent>
    <CardActions>
      <Skeleton variant="rounded" width={100} height={36} />
      <Skeleton variant="rounded" width={80} height={36} />
    </CardActions>
  </Card>
);

// ANNOUNCEMENT: isAnnouncement changes the wording. Announcement drafts
// publish on approval (scheduledraft.js), not at the optimal slot.
const ScheduleDialog = ({ open, onClose, onConfirm, loading, draftType, isAnnouncement = false }) => {
  const isImmediate = ['faq', 'review_response', 'bio'].includes(draftType);
  const [scheduleOption, setScheduleOption] = useState('optimal');
  const [customDateTime, setCustomDateTime] = useState('');

  const minDateTime = new Date(Date.now() + 2 * 60 * 1000)
    .toISOString().slice(0, 16);

  const handleConfirm = () => {
    if (isImmediate || scheduleOption === 'optimal') {
      onConfirm(null);
    } else {
      onConfirm(customDateTime ? new Date(customDateTime).toISOString() : null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isImmediate ? 'Approve & Publish' : 'Approve & Schedule'}
      </DialogTitle>
      <DialogContent>
        {isImmediate ? (
          <Alert severity="info" sx={{ mt: 1 }}>
            This content will be marked as approved and published immediately to your website.
          </Alert>
        ) : (
          <Box sx={{ mt: 1 }}>
            <TextField
              select
              fullWidth
              label="When to publish"
              value={scheduleOption}
              onChange={(e) => setScheduleOption(e.target.value)}
              sx={{ mb: 2 }}
            >
              <MenuItem value="optimal">
                {isAnnouncement ? 'As soon as I approve' : 'Optimal time (recommended by Revfer)'}
              </MenuItem>
              <MenuItem value="custom">Choose a specific time</MenuItem>
            </TextField>

            {scheduleOption === 'custom' && (
              <TextField
                fullWidth
                type="datetime-local"
                label="Publish at"
                value={customDateTime}
                onChange={(e) => setCustomDateTime(e.target.value)}
                inputProps={{ min: minDateTime }}
                InputLabelProps={{ shrink: true }}
              />
            )}

            {scheduleOption === 'optimal' && (
              <Alert severity="success">
                {isAnnouncement
                  ? 'This is time-sensitive, so Revfer will post it right away.'
                  : 'Revfer will automatically post at the best time for your platform.'}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleConfirm}
          disabled={loading || (scheduleOption === 'custom' && !customDateTime)}
          startIcon={loading
            ? <CircularProgress size={16} color="inherit" />
            : <ApproveIcon />
          }
        >
          {loading ? 'Approving...' : 'Confirm Approval'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DraftCard = ({
  draft,
  socialAccounts,
  onApprove,
  onReject,
  onEdit,
  approveLoading,
  rejectLoading,
  businessDefaultAccounts,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);

  // ===== edit state =====
  const [editing, setEditing]       = useState(false);
  const [editText, setEditText]     = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError]   = useState('');

  // draft.content is the pristine original and is never overwritten —
  // Restore sends it back verbatim so the server recognises the revert.
  const original = draft.content || '';
  const edited   = draft.editedContent || null;
  const shown    = edited ?? original;

  const platform  = draft.platform || draft.metadata?.platform;
  const draftType = draft.draftType;

  const hasDefaultAccount = platform === 'website' || !!businessDefaultAccounts?.[platform];
  const isImmediate  = ['faq', 'review_response', 'bio'].includes(draftType);
  const isProcessing = approveLoading || rejectLoading;

  const formattedDate = draft.createdAt
    ? new Intl.DateTimeFormat('en-US', {
        month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit'
      }).format(new Date(draft.createdAt))
    : '';

  const formattedScheduled = draft.scheduledAt
    ? new Intl.DateTimeFormat('en-US', {
        month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit'
      }).format(new Date(draft.scheduledAt))
    : null;

  // ===== edit handlers =====
  const saveEdit = async (text) => {
    if (!text.trim()) {
      setEditError('Content cannot be empty');
      return;
    }
    if (text.length > MAX_CONTENT_LENGTH) {
      setEditError(`Too long — ${text.length} of ${MAX_CONTENT_LENGTH} characters`);
      return;
    }
    setEditError('');
    setEditSaving(true);
    try {
      await onEdit(draft._id, text);
      setEditing(false);
    } catch (e) {
      setEditError(e?.data?.message || 'Failed to save changes');
    } finally {
      setEditSaving(false);
    }
  };

  const openEditor = () => {
    setEditText(shown);
    setEditError('');
    setEditing(true);
  };

  // ===== Real review data from draft =====
  const originalReview = draft.originalReview || null;

  const sourceColors = {
    google:    '#4285F4',
    facebook:  '#1877F2',
    instagram: '#E4405F',
    revfer:    '#6C63FF',
    yelp:      '#D32323',
    tripadvisor: '#34E0A1',
    trustpilot:  '#00B67A'
  };

  const getRelativeTime = (dateStr) => {
    if (!dateStr) return null;
    try {
      const diff  = Date.now() - new Date(dateStr).getTime();
      const days  = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor(diff / (1000 * 60 * 60));
      if (days > 0)  return `${days} day${days > 1 ? 's' : ''} ago`;
      if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
      return 'Just now';
    } catch {
      return null;
    }
  };

  const SourceBadge = ({ source }) => {
    const label = source
      ? source.charAt(0).toUpperCase() + source.slice(1)
      : 'Revfer';
    return (
      <Chip
        label={label}
        size="small"
        sx={{
          height:     18,
          fontSize:   '0.65rem',
          fontWeight: 600,
          bgcolor:    `${sourceColors[source] || sourceColors.revfer}15`,
          color:      sourceColors[source]    || sourceColors.revfer,
          border:     `1px solid ${sourceColors[source] || sourceColors.revfer}40`,
        }}
      />
    );
  };

  const StarRating = ({ rating }) => (
    <Box sx={{ display: 'flex', gap: 0.25 }}>
      {[1, 2, 3, 4, 5].map(star => (
        <Box
          key={star}
          sx={{
            width:    12,
            height:   12,
            bgcolor:  star <= rating ? '#FFC107' : 'grey.300',
            clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)'
          }}
        />
      ))}
    </Box>
  );

  const headerSource = originalReview?.source
    || (draft.sourceSignalType === 'review' ? 'revfer' : null);

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          mb: 2,
          borderColor: draft.status === 'pending_approval' ? 'warning.light' : 'divider',
          transition: 'box-shadow 0.2s',
          '&:hover': { boxShadow: 2 }
        }}
      >
        {/* Image */}
        {draft.imageS3Url && (
          <CardMedia
            component="img"
            height={isMobile ? 160 : 200}
            image={draft.imageS3Url}
            alt="Draft image"
            sx={{ objectFit: 'cover' }}
          />
        )}

        <CardContent sx={{ pb: 1 }}>

          {/* Header row */}
          <Box sx={{
            display: 'flex', gap: 1,
            flexWrap: 'wrap', alignItems: 'center', mb: 1.5
          }}>
            <DraftTypeBadge draftType={draftType} />
            {platform && <PlatformChip platform={platform} />}
            {headerSource && <SourceBadge source={headerSource} />}
            {!hasDefaultAccount && platform && platform !== 'website' && (
              <NoAccountWarning platform={platform} />
            )}
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              {formattedDate}
            </Typography>
          </Box>

          {/* ===== ANNOUNCEMENT: From your update ===== */}
          {draft.announcementId && draft.announcement && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'primary.50', borderRadius: 1,
                       borderLeft: '3px solid', borderLeftColor: 'primary.main' }}>
              <Typography variant="caption" fontWeight={700} color="primary.main"
                          sx={{ display: 'block', mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                From your update
              </Typography>
              <AnnouncementItems
                items={[{ kind:    draft.announcement.kind,
                          summary: draft.announcement.summary,
                          window:  draft.announcement.window,
                          flags:   draft.announcement.flags,
                          facts:   [] }]}
              />
            </Box>
          )}

          {/* ===== Original Review Section ===== */}
          {draftType === 'review_response' && originalReview && (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                bgcolor: 'grey.50',
                borderRadius: 1,
                borderLeft: '3px solid',
                borderLeftColor: sourceColors[originalReview.source] || sourceColors.revfer
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                <SourceBadge source={originalReview.source} />
                {originalReview.rating && (
                  <StarRating rating={originalReview.rating} />
                )}
                <Typography variant="caption" color="text.secondary">
                  {originalReview.reviewerName}
                </Typography>
                {originalReview.createdAt && (
                  <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>
                    {getRelativeTime(originalReview.createdAt)}
                  </Typography>
                )}
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontStyle: 'italic', lineHeight: 1.5 }}
              >
                "{originalReview.text}"
              </Typography>
            </Box>
          )}

          {/* Placeholder if review_response but no originalReview data */}
          {draftType === 'review_response' && !originalReview && (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                bgcolor: 'grey.50',
                borderRadius: 1,
                borderLeft: '3px solid',
                borderLeftColor: 'grey.300'
              }}
            >
              <Typography variant="caption" color="text.disabled" fontStyle="italic">
                Original review not available for this draft
              </Typography>
            </Box>
          )}

          {/* AI Reply label */}
          {draftType === 'review_response' && (
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              sx={{
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                display: 'block',
                mb: 0.75
              }}
            >
              AI Generated Reply
            </Typography>
          )}

          {/* Title for FAQs */}
          {draft.title && (
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              {draft.title}
            </Typography>
          )}

          {/* ===== Content — read mode or edit mode ===== */}
          {editing ? (
            <Box sx={{ mt: 1 }}>
              <TextField
                fullWidth
                multiline
                minRows={5}
                autoFocus
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                disabled={editSaving}
                inputProps={{ maxLength: MAX_CONTENT_LENGTH }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.5 }}
              >
                {editText.length} characters · saving does not approve this draft
              </Typography>
              {editError && (
                <Alert severity="error" sx={{ mt: 1 }} onClose={() => setEditError('')}>
                  {editError}
                </Alert>
              )}
              <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                <Button
                  size="small"
                  variant="contained"
                  disabled={editSaving || !editText.trim()}
                  startIcon={editSaving
                    ? <CircularProgress size={14} color="inherit" />
                    : <SaveIcon />}
                  onClick={() => saveEdit(editText)}
                >
                  {editSaving ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  size="small"
                  disabled={editSaving}
                  onClick={() => { setEditing(false); setEditError(''); }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          ) : (
            <>
              <Typography
                variant="body2"
                color="text.primary"
                sx={{
                  lineHeight: 1.6,
                  display: '-webkit-box',
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  whiteSpace: 'pre-wrap'
                }}
              >
                {shown}
              </Typography>
              {edited && (
                <Chip
                  label="Edited"
                  size="small"
                  color="info"
                  variant="outlined"
                  sx={{ mt: 1, height: 20, fontSize: '0.7rem' }}
                />
              )}
            </>
          )}

          {/* Signal source */}
          {draft.sourceSignalType && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: 'block' }}
            >
              Source: {draft.sourceSignalType.replace(/_/g, ' ')}
            </Typography>
          )}

          {/* Scheduled time */}
          {formattedScheduled && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
              <ScheduleIcon fontSize="small" color="info" />
              <Typography variant="caption" color="info.main">
                Scheduled for {formattedScheduled}
              </Typography>
            </Box>
          )}

        </CardContent>

        {/* Actions */}
        {draft.status === 'pending_approval' && (
          <>
            <Divider />
            <CardActions sx={{ px: 2, py: 1.5, gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                color="success"
                size="small"
                startIcon={isImmediate ? <ApproveIcon /> : <ScheduleIcon />}
                onClick={() => setScheduleDialogOpen(true)}
                disabled={isProcessing || editing || editSaving}
                sx={{ flex: isMobile ? 1 : 'unset' }}
              >
                {isImmediate ? 'Approve' : 'Approve & Schedule'}
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<RejectIcon />}
                onClick={() => onReject(draft._id)}
                disabled={isProcessing || editing || editSaving}
                sx={{ flex: isMobile ? 1 : 'unset' }}
              >
                Reject
              </Button>
              {!editing && (
                <Button
                  size="small"
                  variant="text"
                  startIcon={<EditIcon />}
                  disabled={isProcessing || editSaving}
                  onClick={openEditor}
                  sx={{ textTransform: 'none' }}
                >
                  Edit
                </Button>
              )}
              {!editing && edited && (
                <Button
                  size="small"
                  variant="text"
                  color="inherit"
                  startIcon={editSaving
                    ? <CircularProgress size={14} color="inherit" />
                    : <RestoreIcon />}
                  disabled={isProcessing || editSaving}
                  onClick={() => saveEdit(original)}
                  sx={{ textTransform: 'none' }}
                >
                  Restore original
                </Button>
              )}
            </CardActions>
          </>
        )}
      </Card>

      <ScheduleDialog
        open={scheduleDialogOpen}
        onClose={() => setScheduleDialogOpen(false)}
        onConfirm={(scheduledAt) => {
          setScheduleDialogOpen(false);
          onApprove(draft._id, draft.draftType, scheduledAt);
        }}
        loading={approveLoading}
        draftType={draft.draftType}
        isAnnouncement={!!draft.announcementId}
      />
    </>
  );
};

// blocked: the business record names nothing concrete, so the kickstart never
// ran and nothing will arrive. "Revfer will generate drafts as activity comes
// in" was untrue for them: 9 of 14 new businesses (Oct 2026) sat on this
// screen, polling, with no reason given.
const EmptyState = ({ status, onRefresh, blocked = false }) => (
  <Box sx={{ textAlign: 'center', py: 8 }}>
    <Typography variant="h6" color="text.secondary" gutterBottom>
      {status === 'pending_approval'
        ? (blocked ? 'No drafts yet' : 'No drafts waiting for approval')
        : `No ${status.replace(/_/g, ' ')} drafts`}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
      {status === 'pending_approval'
        ? (blocked
            ? 'Revfer needs a little more about your business before it can write anything. See the note above.'
            : 'Revfer will generate content drafts as your business receives reviews, questions, and activity.')
        : 'Drafts you approve or reject will appear here.'}
    </Typography>
    <Button
      variant="outlined"
      startIcon={<RefreshIcon />}
      onClick={onRefresh}
    >
      Refresh
    </Button>
  </Box>
);

// ===== Main Page =====

export default function DraftApprovalPage() {
  const { businessId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const thisUser = useSelector((state) => state.auth?.user);
  const token    = useSelector((state) => state.auth?.token || null);

  // Same owner-only endpoint the business page uses for its quality banner.
  // A 403 (not the owner) arrives as absent data, so isBlocked stays false.
  const { data: ownerView } = useGetBusinessOwnerViewQuery(businessId, { skip: !businessId });
  const quality   = ownerView?.businessQuality || null;
  const isBlocked = quality?.tier === 'blocked' && !quality?.ownerConfirmed;

  // The edit form takes its WHOLE data source from router state, and fields
  // missing there are saved as "". So it needs the public profile merged with
  // ownerView, exactly as BusinessOwnerActions does. Fetched only when blocked,
  // and the button stays disabled until it has arrived.
  const { data: profileForEdit } = useGetBusinessByIdQuery(
    { businessId, authType: 'none', token },
    { skip: !businessId || !isBlocked }
  );
  const handleEditBusiness = () => {
    if (!profileForEdit) return;
    navigate(`/business/${businessId}/edit`, { state: { ...profileForEdit, ...(ownerView || {}) } });
  };

  const initialStatus = searchParams.get('status') || 'pending_approval';
  const [activeTab, setActiveTab] = useState(initialStatus);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [processingDraftId, setProcessingDraftId] = useState(null);

  // Queries
  const draftsPoll = usePollingInterval(30000);
  const {
    data: draftsData,
    isLoading: draftsLoading,
    isError: draftsError,
    refetch: refetchDrafts
  } = useGetDraftsQuery(
    { businessId, status: activeTab },
    { skip: !businessId, pollingInterval: draftsPoll }
  );

  const {
    data: socialAccountsData,
    isLoading: accountsLoading
  } = useGetSocialAccountsQuery(undefined, { skip: !businessId });

  // Mutations
  const [approveDraft, { isLoading: approveLoading }] = useApproveDraftMutation();
  const [rejectDraft,  { isLoading: rejectLoading }]  = useRejectDraftMutation();
  const [editDraft] = useEditDraftMutation();

  const defaultAccountsByPlatform = useMemo(() => {
    if (!socialAccountsData?.platformGroups) return {};
    const defaults = {};
    Object.entries(socialAccountsData.platformGroups).forEach(([platform, accounts]) => {
      if (accounts.length > 0) defaults[platform] = accounts[0].accountId;
    });
    return defaults;
  }, [socialAccountsData]);

  const drafts = draftsData?.drafts || [];
  const totalByStatus = useMemo(() => {
    const map = {};
    STATUS_TABS.forEach(t => { map[t.value] = 0; });
    if (draftsData?.total) map[activeTab] = draftsData.total;
    return map;
  }, [draftsData, activeTab]);

  // Handlers
  const handleApprove = async (draftId, draftType, scheduledAt) => {
    try {
      setProcessingDraftId(draftId);
      const res = await approveDraft({ draftId, businessId, scheduledAt }).unwrap();

      // Approved, but parked until an account is connected. Saying "scheduled"
      // here would be the dashboard's version of the bug T2a fixed on the
      // token page: a green confirmation for a post that will not go out.
      if (res?.pendingConnection) {
        setSnackbar({
          open: true,
          message: `Approved. Connect your ${res.platform} account and Revfer will publish it.`,
          severity: 'warning',
          connect: true
        });
        return;
      }

      setSnackbar({
        open: true,
        message: ['faq', 'review_response', 'bio'].includes(draftType)
          ? 'Draft approved and queued for publishing'
          : scheduledAt
            ? `Draft scheduled for ${new Date(scheduledAt).toLocaleString()}`
            : 'Draft approved and scheduled at optimal time',
        severity: 'success'
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.data?.message || 'Failed to approve draft',
        severity: 'error'
      });
    } finally {
      setProcessingDraftId(null);
    }
  };

  const handleReject = async (draftId) => {
    try {
      setProcessingDraftId(draftId);
      await rejectDraft({ draftId, businessId }).unwrap();
      setSnackbar({ open: true, message: 'Draft rejected', severity: 'info' });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.data?.message || 'Failed to reject draft',
        severity: 'error'
      });
    } finally {
      setProcessingDraftId(null);
    }
  };

  // Editing does NOT approve — the draft stays pending_approval and the
  // owner still acts on it separately. Throws on failure so the card can
  // surface the error inline.
  const handleEdit = async (draftId, editedContent) => {
    const res = await editDraft({
      draftId,
      businessId,
      status: activeTab,
      editedContent,
    }).unwrap();

    setSnackbar({
      open: true,
      message: res.edited ? 'Changes saved' : 'Draft restored to original',
      severity: 'success'
    });
    return res;
  };

  const pendingCount = activeTab === 'pending_approval' ? drafts.length : 0;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>

      {/* Header */}
      <AppBar
        position="sticky"
        color="default"
        elevation={0}
        sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            onClick={() => navigate(-1)}
            sx={{ mr: 1 }}
          >
            <BackIcon />
          </IconButton>

          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" fontWeight={600}>
              Content Drafts
            </Typography>
            {!isMobile && (
              <Typography variant="caption" color="text.secondary">
                Review and approve AI-generated content for your business
              </Typography>
            )}
          </Box>

          {pendingCount > 0 && (
            <Badge badgeContent={pendingCount} color="warning" sx={{ mr: 2 }}>
              <Typography variant="body2" color="text.secondary">Pending</Typography>
            </Badge>
          )}

          <IconButton onClick={refetchDrafts} disabled={draftsLoading}>
            <RefreshIcon />
          </IconButton>
        </Toolbar>

        {/* Status Tabs */}
        <Tabs
          value={activeTab}
          onChange={(_, newVal) => setActiveTab(newVal)}
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons="auto"
          sx={{ px: 2 }}
        >
          {STATUS_TABS.map((tab) => (
            <Tab
              key={tab.value}
              value={tab.value}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {tab.label}
                  {tab.value === 'pending_approval' && drafts.length > 0 && activeTab === 'pending_approval' && (
                    <Chip
                      label={drafts.length}
                      size="small"
                      color="warning"
                      sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }}
                    />
                  )}
                </Box>
              }
            />
          ))}
        </Tabs>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 3, px: { xs: 2, sm: 3 } }}>
        <Grid container spacing={3}>

          {/* Main Content */}
          <Grid item xs={12} md={8}>

            {/* Blocked: say why there is nothing here, and what fixes it */}
            {isBlocked && (
              <Alert
                severity="warning"
                sx={{ mb: 3 }}
                action={
                  <Button
                    size="small"
                    color="warning"
                    variant="contained"
                    startIcon={<EditIcon />}
                    disabled={!profileForEdit}
                    onClick={handleEditBusiness}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Add details
                  </Button>
                }
              >
                <Typography variant="subtitle2" fontWeight={700}>
                  Revfer needs a bit more about your business
                </Typography>
                <Typography variant="body2">
                  {quality?.summary
                    || 'Name two or three things you sell or offer, so Revfer has something real to write about.'}
                  {' '}Once you add it, Revfer can start writing your posts.
                </Typography>
              </Alert>
            )}

            {/* Social Account Warning Banner. Hidden while blocked: connecting
                an account is pointless until there is something to post. */}
            {activeTab === 'pending_approval' &&
              !isBlocked &&
              !accountsLoading &&
              (!socialAccountsData || socialAccountsData.totalAccounts === 0) && (
              <Alert
                severity="warning"
                sx={{ mb: 3 }}
                action={
                  <Button
                    size="small"
                    color="warning"
                    onClick={() => navigate(`/business/${businessId}/social/accounts`,
                      { state: { returnPath: window.location.pathname } })}
                  >
                    Connect Now
                  </Button>
                }
              >
                Connect Facebook or Instagram so Revfer can post what you approve. Nothing is posted without your approval.
              </Alert>
            )}

            {/* Loading State */}
            {draftsLoading && (
              <>
                {[1, 2, 3].map(i => <DraftCardSkeleton key={i} />)}
              </>
            )}

            {/* Error State */}
            {draftsError && !draftsLoading && (
              <Alert
                severity="error"
                action={
                  <Button size="small" onClick={refetchDrafts}>Retry</Button>
                }
              >
                Failed to load drafts. Please try again.
              </Alert>
            )}

            {/* Empty State */}
            {!draftsLoading && !draftsError && drafts.length === 0 && (
              <EmptyState status={activeTab} onRefresh={refetchDrafts} blocked={isBlocked} />
            )}

            {/* Draft Cards */}
            {!draftsLoading && !draftsError && drafts.map((draft) => (
              <DraftCard
                key={draft._id}
                draft={draft}
                socialAccounts={socialAccountsData?.accounts || []}
                businessDefaultAccounts={defaultAccountsByPlatform}
                onApprove={handleApprove}
                onReject={handleReject}
                onEdit={handleEdit}
                approveLoading={approveLoading && processingDraftId === draft._id}
                rejectLoading={rejectLoading && processingDraftId === draft._id}
              />
            ))}
          </Grid>

          {/* Right Sidebar — desktop only */}
          {!isMobile && (
            <Grid item md={4}>

              {/* Connected Accounts */}
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Connected Accounts
                </Typography>
                {accountsLoading ? (
                  <Skeleton variant="rectangular" height={80} />
                ) : socialAccountsData?.accounts?.length > 0 ? (
                  socialAccountsData.accounts.map((account) => (
                    <Box
                      key={account.accountId}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        py: 1,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        '&:last-child': { borderBottom: 'none' }
                      }}
                    >
                      <Box sx={{
                        color: PLATFORM_CONFIG[account.platform]?.color || 'text.secondary'
                      }}>
                        {PLATFORM_CONFIG[account.platform]?.icon}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" noWrap fontWeight={500}>
                          {account.accountName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {PLATFORM_CONFIG[account.platform]?.label}
                        </Typography>
                      </Box>
                      <Chip
                        label={account.isHealthy ? 'Active' : 'Disconnected'}
                        size="small"
                        color={account.isHealthy ? 'success' : 'error'}
                        variant="outlined"
                      />
                    </Box>
                  ))
                ) : (
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      No accounts connected
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                    onClick={() => navigate(`/business/${businessId}/social/accounts`,
                      { state: { returnPath: window.location.pathname } })}
                  >
                    Connect Now
                    </Button>
                  </Box>
                )}
              </Paper>

              {/* Quick Stats */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Draft Summary
                </Typography>
                {STATUS_TABS.map((tab) => (
                  <Box
                    key={tab.value}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 0.75,
                      cursor: 'pointer',
                      borderRadius: 1,
                      px: 1,
                      bgcolor: activeTab === tab.value ? 'action.selected' : 'transparent',
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                    onClick={() => setActiveTab(tab.value)}
                  >
                    <Typography variant="body2">
                      {tab.label}
                    </Typography>
                    {activeTab === tab.value && (
                      <Chip
                        label={drafts.length}
                        size="small"
                        color={tab.color}
                        variant="outlined"
                      />
                    )}
                  </Box>
                ))}
              </Paper>

            </Grid>
          )}
        </Grid>
      </Container>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.connect ? null : 4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
          action={snackbar.connect ? (
            <Button color="inherit" size="small" sx={{ fontWeight: 700 }}
              onClick={() => navigate(`/business/${businessId}/social/accounts`,
                { state: { returnPath: window.location.pathname } })}>
              Connect
            </Button>
          ) : undefined}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

    </Box>
  );
}